import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SignaturePad from 'signature_pad';
import {
  ArrowRight,
  Check,
  Download,
  ExternalLink,
  FileText,
  IdCard,
  Loader2,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { api, ApiError, fetchLeaseFileBytes, type LeaseInfo } from '@/lib/api';
import { REAUTH_CODE } from '@/lib/google';
import { ConfirmIdentity } from '@/components/ConfirmIdentity';
import { BrandBand } from '@/components/BrandBand';
import { useBrand } from '@/lib/brand';
import { leaseDownloadName } from '@/lib/leaseFile';
// pdf.js is large: load it only when a lease is actually shown.
const PdfPages = lazy(() => import('@/components/PdfPages').then((m) => ({ default: m.PdfPages })));

// The portal's action colour (brand-500): TendedLoop teal, or the building's brand.
const TEAL = 'rgb(var(--brand-500))';
// The lease names one field differently from the wizard.
const LEASE_TO_WIZARD_FIELD: Record<string, string> = { idNumber: 'nationalId' };

/** The wizard field "complete your details" opens: the ID first when the signer can't be identified yet. */
function completeDetailsField(lease: Pick<LeaseInfo, 'identityComplete' | 'missingDetails'>): string {
  if (!lease.identityComplete) return 'nationalId';
  const key = lease.missingDetails[0]?.key ?? '';
  return LEASE_TO_WIZARD_FIELD[key] ?? key;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  // A bare YYYY-MM-DD is a calendar date (UTC midnight): format it in UTC so it
  // never shifts a day in another timezone.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return new Intl.DateTimeFormat('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  }).format(d);
}

async function sha256Hex(buf: ArrayBuffer): Promise<string | undefined> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined; // insecure context (plain http on a LAN IP): the server still hashes what it seals
  }
}

function SignatureField({
  padRef,
  onEmptyChange,
}: {
  padRef: React.MutableRefObject<SignaturePad | null>;
  onEmptyChange: (empty: boolean) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pad = new SignaturePad(canvas, { penColor: '#1e3a8a', backgroundColor: 'rgba(255,255,255,0)' });
    padRef.current = pad;
    const report = (e: boolean) => {
      setEmpty(e);
      onEmptyChange(e);
    };
    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const data = pad.toData();
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
      pad.clear();
      if (data.length) pad.fromData(data);
      report(pad.isEmpty());
    };
    resize();
    window.addEventListener('resize', resize);
    const onEnd = () => report(pad.isEmpty());
    pad.addEventListener('endStroke', onEnd);
    return () => {
      window.removeEventListener('resize', resize);
      pad.removeEventListener('endStroke', onEnd);
    };
  }, [padRef, onEmptyChange]);

  const clear = () => {
    padRef.current?.clear();
    setEmpty(true);
    onEmptyChange(true);
  };

  return (
    <div>
      <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-white">
        <canvas ref={canvasRef} className="h-40 w-full rounded-xl" style={{ touchAction: 'none' }} />
        {empty && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400">
            חתמו כאן באצבע
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        className="mt-2 flex items-center gap-1.5 text-[15px] text-slate-500 hover:text-slate-700"
      >
        <RotateCcw size={16} /> ניקוי
      </button>
    </div>
  );
}

export function LeasePage() {
  const navigate = useNavigate();
  // The building's band (null keeps TendedLoop's look) above the page's own header.
  const brand = useBrand((s) => s.brand);
  // The org handle of a verified brand names the copy the resident keeps.
  const handle = useBrand((s) => (s.brand ? s.handle : null));
  const [lease, setLease] = useState<LeaseInfo | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  // The contract shows the resident's ID number: it opens after a recent sign-in.
  const [needConfirm, setNeedConfirm] = useState<null | 'view' | 'sign'>(null);
  const onRenderError = useCallback(() => setRenderFailed(true), []);
  const padRef = useRef<SignaturePad | null>(null);
  const urlRef = useRef<string | null>(null);
  const shaRef = useRef<string | undefined>(undefined);

  const loadPdf = useCallback(async () => {
    setPdfError(false);
    setNeedConfirm(null);
    try {
      const buf = await fetchLeaseFileBytes();
      shaRef.current = await sha256Hex(buf);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
      urlRef.current = url;
      setPdfUrl(url);
      setPdfBytes(buf);
      setRenderFailed(false);
    } catch (e) {
      // The lease details still show; signing waits until the document itself loads.
      setPdfUrl(null);
      if (e instanceof ApiError && e.code === REAUTH_CODE) setNeedConfirm('view');
      else setPdfError(true);
    }
  }, []);

  const load = useCallback(async (): Promise<LeaseInfo | null> => {
    setLoading(true);
    setError(null);
    try {
      const info = await api.getLease();
      setLease(info);
      if (info.status === 'signed') setDone(true);
      if (info.status !== 'none' && !info.signedOffline) await loadPdf();
      return info;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadPdf]);

  useEffect(() => {
    void load();
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [load]);

  const downloadSigned = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = leaseDownloadName(handle, lease?.contractNumber);
    a.click();
  };

  const submit = async () => {
    const pad = padRef.current;
    if (!consent) {
      setError('יש לאשר את ההצהרה לפני החתימה');
      return;
    }
    if (!pad || pad.isEmpty()) {
      setError('נא להוסיף חתימה');
      return;
    }
    if (!pdfUrl) {
      setError('יש לטעון ולקרוא את החוזה לפני החתימה');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.signLease(pad.toDataURL('image/png'), shaRef.current);
      await load();
      setDone(true);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'NETWORK') {
        // The drawn signature stays on the pad; nothing was sent.
        setError('אין חיבור לאינטרנט כרגע. החתימה שלך נשארה בדף; נסו שוב בעוד רגע.');
        return;
      }
      if (e instanceof ApiError && e.code === REAUTH_CODE) {
        // More than half an hour since the resident confirmed: ask again, then they sign.
        setNeedConfirm('sign');
        return;
      }
      if (e instanceof ApiError && e.code === 'LEASE_DOCUMENT_CHANGED') {
        // The operator replaced the lease: show the new version and ask again.
        setConsent(false);
        padRef.current?.clear();
        setSigEmpty(true);
        const fresh = await load();
        if (fresh?.status === 'none') {
          // The housing team changed the contract terms: a new version is coming.
          setError('צוות הדיור עדכן את תנאי החוזה, והוא יישלח אליך שוב לחתימה.');
          return;
        }
      }
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // The band scrolls away; the back button and the title stay pinned.
  const Header = (
    <>
      {brand && <BrandBand brand={brand} size="page" />}
      {/* A plain bar, not a second <header>: the band is the page's one banner. */}
      {/* Under the full band it takes the page's own colour, so band, splash and page read as one. */}
      <div
        className={
          'sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 px-4 py-3 backdrop-blur ' +
          (brand ? 'bg-slate-50/95' : 'bg-white/95')
        }
      >
        <button
          onClick={() => navigate('/')}
          aria-label="חזרה"
          className="-ms-2 flex h-12 w-12 items-center justify-center rounded-lg hover:bg-slate-100"
        >
          <ArrowRight size={22} className="text-slate-700" />
        </button>
        <h1 className="text-lg font-bold text-brand-900">{lease?.documentTitle || 'חוזה השכירות'}</h1>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {Header}
        <div className="flex flex-col items-center justify-center py-24 text-slate-500">
          <Loader2 className="animate-spin" size={28} />
          <p className="mt-3 text-base">טוען…</p>
        </div>
      </div>
    );
  }

  if (done || lease?.status === 'signed') {
    return (
      <div className="min-h-screen bg-slate-50">
        {Header}
        <div className="mx-auto max-w-lg px-4 py-10 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-100">
            <Check size={34} className="text-done" />
          </div>
          <h2 className="mt-5 text-2xl font-bold text-brand-900">החוזה נחתם בהצלחה</h2>
          <p className="mt-2 text-base text-slate-600">
            {lease?.signedOffline ? 'לעותק של החוזה, פנו לצוות הדיור.' : 'שמרו עותק לעצמכם.'}
          </p>
          {/* The document the resident keeps: whose it is, when it was signed, and the copy. */}
          <div className="mt-6 rounded-2xl bg-white p-4 text-start shadow-sm ring-1 ring-gray-100">
            <div className="flex items-start gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50">
                <FileText size={24} className="text-brand-600" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-[17px] font-bold text-brand-900">חוזה {lease?.contractNumber}</p>
                {brand?.displayName && <p className="text-[15px] text-slate-600">{brand.displayName}</p>}
                <p className="text-[15px] text-slate-600">
                  {lease?.signedOffline ? 'נחתם מול צוות הדיור' : 'נחתם דיגיטלית'}
                  {lease?.signedAt ? ` בתאריך ${formatDate(lease.signedAt)}` : ''}
                </p>
              </div>
            </div>
            {pdfUrl && (
              <button
                onClick={downloadSigned}
                className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl px-5 text-base font-semibold text-white"
                style={{ backgroundColor: TEAL }}
              >
                <Download size={20} /> הורדת העותק החתום
              </button>
            )}
          </div>
          <button onClick={() => navigate('/')} className="mt-3 w-full py-3 text-base font-medium text-slate-600">
            חזרה לעמוד הראשי
          </button>
          {error && <p className="mt-4 text-[15px] text-red-600">{error}</p>}
        </div>
      </div>
    );
  }

  if (!lease) {
    // The lease could not be loaded at all (network / server error).
    return (
      <div className="min-h-screen bg-slate-50">
        {Header}
        <div className="mx-auto max-w-lg px-4 py-16 text-center text-slate-600" role="alert">
          <FileText size={40} className="mx-auto text-slate-400" />
          <p className="mt-4 text-lg font-medium text-slate-800">לא הצלחנו לטעון את החוזה</p>
          {error && <p className="mt-1 text-base">{error}</p>}
          <button
            onClick={() => void load()}
            className="mt-6 w-full rounded-xl px-5 py-3.5 text-base font-semibold text-white"
            style={{ backgroundColor: TEAL }}
          >
            נסו שוב
          </button>
        </div>
      </div>
    );
  }

  if (lease.status === 'none') {
    return (
      <div className="min-h-screen bg-slate-50">
        {Header}
        <div className="mx-auto max-w-lg px-4 py-16 text-center text-slate-600">
          <FileText size={40} className="mx-auto text-slate-400" />
          <p className="mt-4 text-lg font-medium text-slate-800">אין חוזה הממתין לחתימה</p>
          <p className="mt-1 text-base">
            {lease.coveredByPartner
              ? 'החוזה על הדירה חתום על ידי בן/בת הזוג, ואין צורך בחתימה שלכם.'
              : 'כשצוות הדיור יעלה עבורכם חוזה, הוא יופיע כאן לחתימה.'}
          </p>
          {error && (
            <p className="mt-3 rounded-lg bg-amber-50 p-3 text-base text-amber-900" role="status">
              {error}
            </p>
          )}
          <button onClick={() => navigate('/')} className="mt-6 text-base font-semibold" style={{ color: TEAL }}>
            חזרה לעמוד הראשי
          </button>
        </div>
      </div>
    );
  }

  const ready = consent && !sigEmpty && !!pdfUrl;
  const canSign = lease.identityComplete && lease.missingDetails.length === 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      {Header}
      <div className="mx-auto max-w-2xl px-4 py-5">
        {/* Summary */}
        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center gap-2 text-slate-900">
            <FileText size={20} style={{ color: TEAL }} />
            <span className="text-lg font-bold">חוזה {lease?.contractNumber}</span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3 text-[15px]">
            {lease?.monthlyRent && (
              <div>
                <dt className="text-slate-500">תשלום חודשי</dt>
                <dd className="font-semibold text-slate-900">
                  {lease.monthlyTotal || Number(lease.monthlyRent).toLocaleString('he-IL')} ₪
                </dd>
              </div>
            )}
            {/* A template document states its own deposit terms; don't show a second figure. */}
            {lease?.depositAmount && !lease.fromTemplate && (
              <div>
                <dt className="text-slate-500">פיקדון</dt>
                <dd className="font-semibold text-slate-900">
                  {Number(lease.depositAmount).toLocaleString('he-IL')} ₪
                </dd>
              </div>
            )}
            {(lease?.startDate || lease?.endDate) && (
              <div>
                <dt className="text-slate-500">תקופה</dt>
                <dd className="font-semibold text-slate-900">
                  {formatDate(lease?.startDate ?? null)}
                  {lease?.endDate ? ` – ${formatDate(lease.endDate)}` : ''}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Can't sign yet: what to complete comes first, before the document fills the screen. */}
        {!canSign && (
          // A signature must be attributable (name + ת"ז or passport), and a template
          // document needs the resident's own details written in before signing.
          <div className="mt-6 rounded-2xl bg-amber-50 p-5 ring-1 ring-amber-200">
            <div className="flex items-center gap-2 text-amber-900">
              <IdCard size={22} />
              <h2 className="text-base font-bold">
                {lease.identityComplete ? 'לפני החתימה: השלימו את הפרטים' : 'לפני החתימה: השלימו את הפרטים המזהים'}
              </h2>
            </div>
            <p className="mt-2 text-base text-amber-900">
              {lease.identityComplete
                ? `הפרטים האלה מופיעים במסמך וחסרים עדיין: ${lease.missingDetails.map((m) => m.label).join(', ')}.`
                : 'כדי שהחתימה תהיה שלכם, נדרשים שם מלא ומספר תעודת זהות או דרכון.'}
            </p>
            <button
              onClick={() => navigate(`/?field=${completeDetailsField(lease)}`)}
              className="mt-4 w-full rounded-xl px-5 py-3.5 text-base font-semibold text-white"
              style={{ backgroundColor: TEAL }}
            >
              להשלמת הפרטים
            </button>
          </div>
        )}

        {/* Document */}
        <div className="mt-6 mb-2 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">קראו את החוזה לפני החתימה</h2>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[15px] font-semibold"
              style={{ color: TEAL }}
            >
              <ExternalLink size={16} /> מסך מלא
            </a>
          )}
        </div>
        {needConfirm ? (
          <ConfirmIdentity
            next="/lease"
            reason={needConfirm === 'sign' ? 'לפני החתימה' : 'כדי לפתוח את החוזה, שכולל את פרטי הזהות שלכם'}
            onConfirmed={() => {
              if (needConfirm === 'sign') {
                setNeedConfirm(null);
                void submit();
              } else void loadPdf();
            }}
          />
        ) : pdfUrl && pdfBytes ? (
          <Suspense
            fallback={
              <div className="flex h-[60vh] items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-[15px] text-slate-500">
                <Loader2 className="animate-spin" size={20} />
              </div>
            }
          >
            <PdfPages data={pdfBytes} onError={onRenderError} />
          </Suspense>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-center" role="alert">
            <p className="text-base text-slate-700">
              {pdfError ? 'לא הצלחנו לטעון את קובץ החוזה.' : 'לא ניתן להציג את החוזה כרגע.'}
            </p>
            <button
              onClick={() => void loadPdf()}
              className="mt-3 min-h-[48px] rounded-xl px-5 text-base font-semibold"
              style={{ color: TEAL }}
            >
              טעינה מחדש
            </button>
          </div>
        )}
        {pdfUrl && renderFailed && (
          <p className="mt-2 text-[15px] text-slate-700" role="alert">
            לא ניתן להציג את החוזה בדף. לחצו על "מסך מלא" כדי לפתוח אותו.
          </p>
        )}

        {canSign && (
          <>
            {/* Consent */}
            <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300"
                style={{ accentColor: TEAL }}
              />
              <span className="text-[15px] leading-relaxed text-slate-700">{lease?.consentText}</span>
            </label>

            {/* Signature */}
            <h2 className="mt-6 mb-2 text-base font-bold text-slate-900">חתימה</h2>
            <SignatureField padRef={padRef} onEmptyChange={setSigEmpty} />

            <p className="mt-4 flex items-center justify-center gap-1.5 text-[15px] text-slate-400">
              <ShieldCheck size={15} /> החתימה נשמרת באופן מאובטח ומוצפן
            </p>
          </>
        )}
        {error && <p className="mt-3 text-center text-[15px] text-red-600">{error}</p>}
      </div>

      {/* Sticky action */}
      {canSign && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 p-4 backdrop-blur">
          <div className="mx-auto max-w-2xl">
            <button
              onClick={submit}
              disabled={submitting}
              aria-disabled={!ready}
              className="flex w-full items-center justify-center gap-2 rounded-xl px-5 py-4 text-base font-semibold text-white transition disabled:cursor-not-allowed"
              style={{ backgroundColor: TEAL, opacity: submitting || !ready ? 0.5 : 1 }}
            >
              {submitting ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
              {submitting ? 'חותם…' : 'חתמו על החוזה'}
            </button>
            {!ready && !submitting && (
              <p className="mt-2 text-center text-[15px] text-slate-500">
                {!pdfUrl
                  ? 'יש לטעון את החוזה לפני החתימה'
                  : !consent
                    ? 'יש לאשר את ההצהרה ולהוסיף חתימה'
                    : 'נא להוסיף חתימה'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
